import { IndexKey, TLShape, TLShapeId, toRichText } from "tldraw";
import {
  convertPartialFocusedShapeToTldrawShape,
  FOCUSED_TO_GEO_TYPES,
} from "../../shared/format/convertFocusedShapeToTldrawShape";
import { FocusedShape } from "../../shared/format/FocusedShape";
import { CreateAction } from "../../shared/schema/AgentActionSchemas";
import { Streaming } from "../../shared/types/Streaming";
import { AgentHelpers } from "../AgentHelpers";
import { AgentActionUtil, registerActionUtil } from "./AgentActionUtil";
import {
  containsInlineLatex,
  convertTextToLatex,
} from "../utils/latexDetection";
import { getPremadeStackingIndex } from "../utils/premadeZOrder";
import {
  AUTHORED_SOURCE,
  isAuthoredContentAction,
  isStudentAuthoredAction,
} from "../utils/authoredContent";
import { annotationMeta } from "../utils/annotationContent";

export const CreateActionUtil = registerActionUtil(
  class CreateActionUtil extends AgentActionUtil<CreateAction> {
    static override type = "create" as const;

    override getInfo(action: Streaming<CreateAction>) {
      return {
        icon: "pencil" as const,
        description: action.intent ?? "",
      };
    }

    override sanitizeAction(
      action: Streaming<CreateAction>,
      helpers: AgentHelpers,
    ) {
      const { shape } = action;

      // If there's no shape yet, return action (will be filtered in applyAction)
      if (!shape) return action;

      // Auto-convert text shapes that contain inline LaTeX into math shapes
      // so the content renders via KaTeX instead of displaying raw delimiters.
      if (
        shape._type === "text" &&
        "text" in shape &&
        typeof shape.text === "string" &&
        containsInlineLatex(shape.text)
      ) {
        const latex = convertTextToLatex(shape.text);
        action.shape = {
          _type: "math" as const,
          latex,
          shapeId: shape.shapeId,
          note: ("note" in shape ? shape.note : "") ?? "",
          color: ("color" in shape ? shape.color : "black") ?? "black",
          x: "x" in shape ? shape.x : 0,
          y: "y" in shape ? shape.y : 0,
          ...("fontSize" in shape &&
            shape.fontSize !== undefined && { fontSize: shape.fontSize }),
        } as any;
        return action;
      }

      // Ensure the created shape has a unique ID (only if shapeId is present)
      if (shape.shapeId) {
        shape.shapeId = helpers.ensureShapeIdIsUnique(shape.shapeId);
      }

      // Draw/highlight shapes require encoded segments and can only be created
      // via the pen/highlight actions. Returning null here drops the action
      // before it reaches act(), so it never crashes tldraw.
      if (shape._type === "draw" || shape._type === "highlight") return null;

      // If the shape is an arrow, ensure the from and to IDs are real shapes
      if (shape._type === "arrow") {
        if (shape.fromId) {
          shape.fromId = helpers.ensureShapeIdExists(shape.fromId);
        }
        if (shape.toId) {
          shape.toId = helpers.ensureShapeIdExists(shape.toId);
        }
        if ("x1" in shape) {
          shape.x1 = helpers.ensureValueIsNumber(shape.x1) ?? 0;
        }
        if ("y1" in shape) {
          shape.y1 = helpers.ensureValueIsNumber(shape.y1) ?? 0;
        }
        if ("x2" in shape) {
          shape.x2 = helpers.ensureValueIsNumber(shape.x2) ?? 0;
        }
        if ("y2" in shape) {
          shape.y2 = helpers.ensureValueIsNumber(shape.y2) ?? 0;
        }
        if ("bend" in shape) {
          shape.bend = helpers.ensureValueIsNumber(shape.bend) ?? 0;
        }
      }

      return action;
    }

    override applyAction(action: Streaming<CreateAction>) {
      const { editor } = this;
      const { shape } = action;

      // If there's no shape yet, return early
      if (!shape || !shape._type) return;

      // No local grid snap: the gateway already placed this on a rule line.
      const result = convertPartialFocusedShapeToTldrawShape(editor, shape, {
        defaultShape: getDefaultShape(shape._type),
        complete: action.complete,
      });

      if (!result.shape) return;

      // Digitized paper work is the student's own, so it must erase like their ink.
      const studentAuthored = isStudentAuthoredAction(action);

      // Tag the designer's practice problem, seeded at connection open (see
      // isAuthoredContentAction). The tag drives the student delete-guard in
      // TldrawCanvasAgent and skips math auto-measurement.
      if (!studentAuthored && isAuthoredContentAction(action)) {
        // Layer by the authored zIndex, not creation order.
        const zIndex = (shape as { zIndex?: number }).zIndex;
        result.shape.meta = {
          ...result.shape.meta,
          source: AUTHORED_SOURCE,
          ...(typeof zIndex === "number" ? { zIndex } : {}),
        };
        // The student may not select or move the problem statement; the agent
        // still can (it acts under `ignoreShapeLock`). Locking is what stops
        // the drag — the delete-guard alone left the question draggable off
        // the page.
        result.shape.isLocked = true;
        if (typeof zIndex === "number") {
          const stackingIndex = getPremadeStackingIndex(editor, zIndex);
          if (stackingIndex) result.shape.index = stackingIndex;
        }
      }

      // Stamp AI authorship on the shape itself (meta.role = "agent") so the
      // gateway can classify it as `[you/…]` even after its in-memory scene
      // registration is lost (WS reconnect / gateway restart). Mirrors the
      // "spaces-agent" stamp in Spaces mode (useSpaceAgent).
      editor.createShape(
        studentAuthored
          ? { ...result.shape, isLocked: false }
          : {
              ...result.shape,
              // Tags commentary verbs so the screenshot can lift them off ink.
              meta: {
                ...(result.shape.meta ?? {}),
                role: "agent",
                ...annotationMeta(action),
              },
            },
      );

      // Handle arrow bindings if they exist
      if (result.bindings) {
        for (const binding of result.bindings) {
          editor.createBinding({
            type: binding.type,
            fromId: binding.fromId,
            toId: binding.toId,
            props: binding.props,
            meta: binding.meta,
          });
        }
      }
    }
  },
);

function getDefaultShape(shapeType: FocusedShape["_type"]): Partial<TLShape> {
  const isGeo = shapeType in FOCUSED_TO_GEO_TYPES;
  return isGeo
    ? SHAPE_DEFAULTS.geo
    : (SHAPE_DEFAULTS[shapeType as keyof typeof SHAPE_DEFAULTS] ??
        SHAPE_DEFAULTS.unknown);
}

type BaseShapeDefaults = Pick<
  TLShape,
  "id" | "isLocked" | "opacity" | "rotation" | "meta"
>;

const SHARED_DEFAULTS: BaseShapeDefaults = {
  isLocked: false,
  opacity: 1,
  rotation: 0,
  meta: {},
  id: "shape:shape" as TLShapeId,
};

type ShapeDefaults<T extends TLShape = TLShape> = BaseShapeDefaults & {
  props?: Partial<T["props"]>;
};

type SupportedShapeTypes =
  | "text"
  | "line"
  | "arrow"
  | "geo"
  | "note"
  | "draw"
  | "math"
  | "image";

type ShapeDefaultsMap = {
  [K in SupportedShapeTypes]: ShapeDefaults<TLShape<K>>;
} & {
  unknown: ShapeDefaults;
};

const SHAPE_DEFAULTS: ShapeDefaultsMap = {
  text: {
    ...SHARED_DEFAULTS,
    props: {
      autoSize: true,
      color: "black",
      font: "draw",
      richText: toRichText(""),
      scale: 1,
      size: "s",
      textAlign: "start",
      w: 100,
    },
  },
  line: {
    ...SHARED_DEFAULTS,
    props: {
      size: "s",
      color: "black",
      dash: "draw",
      points: {
        a1: {
          id: "a1",
          index: "a1" as IndexKey,
          x: 0,
          y: 0,
        },
        a2: {
          id: "a2",
          index: "a2" as IndexKey,
          x: 100,
          y: 0,
        },
      },
      scale: 1,
      spline: "line",
    },
  },
  arrow: {
    ...SHARED_DEFAULTS,
    props: {
      arrowheadEnd: "arrow",
      arrowheadStart: "none",
      bend: 0,
      color: "black",
      dash: "draw",
      elbowMidPoint: 0.5,
      end: { x: 100, y: 0 },
      fill: "none",
      font: "draw",
      kind: "arc",
      labelColor: "black",
      labelPosition: 0.5,
      richText: toRichText(""),
      scale: 1,
      size: "s",
      start: { x: 0, y: 0 },
    },
  },
  geo: {
    ...SHARED_DEFAULTS,
    props: {
      align: "middle",
      color: "black",
      dash: "draw",
      fill: "none",
      font: "draw",
      geo: "rectangle",
      growY: 0,
      h: 100,
      labelColor: "black",
      richText: toRichText(""),
      scale: 1,
      size: "s",
      url: "",
      verticalAlign: "middle",
      w: 100,
    },
  },
  note: {
    ...SHARED_DEFAULTS,
    props: {
      color: "black",
      richText: toRichText(""),
      size: "s",
      align: "middle",
      font: "draw",
      fontSizeAdjustment: 0,
      growY: 0,
      labelColor: "black",
      scale: 1,
      url: "",
      verticalAlign: "middle",
    },
  },
  draw: {
    ...SHARED_DEFAULTS,
    props: {},
  },
  math: {
    ...SHARED_DEFAULTS,
    // No w/h defaults: math shapes size to their measured KaTeX content in
    // convertMathShapeToTldrawShape — a placeholder size here would be
    // mistaken for an explicit size and break anchor positioning.
    props: {
      // Mirrors `GEOMETRY.text.defaultFontSizePx` in the gateway
      // (src/canvas-ops/geometry-config.ts). The gateway sends an explicit
      // fontSize on every text/math create it authors, so this is what a create
      // that omits one renders at — keep the two numbers equal.
      latex: "",
      color: "black",
      fontSize: 32,
    },
  },
  image: {
    ...SHARED_DEFAULTS,
    props: {
      w: 100,
      h: 100,
      assetId: null,
      playing: true,
      url: "",
      crop: null,
      flipX: false,
      flipY: false,
      altText: "",
    },
  },
  unknown: {
    ...SHARED_DEFAULTS,
    props: {},
  },
};
